
import { Vue, Component, Prop, toNative, Watch } from 'vue-facing-decorator';
import { EmailTemplate } from '@/entities/Invite/EmailTemplateViewModel';
import ConfigureEmailsHelper from '@/helper/ConfigureEmailsHelper';

@Component
class EmailTemplateRenderer extends Vue {
  @Prop() private templateData!: EmailTemplate;
  @Prop({ default: 0 }) private bottomMargin!: number;
  @Prop({ default: false }) private fitToContainer!: boolean;

  // Shadow Root properties
  private root?: ShadowRoot;
  private resizeObserver?: ResizeObserver;

  // Mounted life cycle hook
  private mounted() {
    this.root = (this.$refs.host as HTMLElement).attachShadow({ mode: 'open' });
    this.RenderShadow();
    this.resizeObserver = new ResizeObserver(() => this.AdjustScale());
    this.resizeObserver.observe(this.$refs.host as HTMLElement);
  }

  // Renders the email template into the shadow DOM and applies necessary styles.
  // Sets the inner HTML of the root to a container with the processed email HTML content.
  // Calls `AdjustScale()` to adjust the layout after images have loaded or if no images are present.
  private RenderShadow() {
    if (!this.root) {
      return;
    }
    const html = this.templateData.ProcessedHtml || '';
    this.root.innerHTML = `<div id="email-root">${html}</div>`;

    // Neutralize outer backgrounds and keep images responsive.
    const style = document.createElement('style');
    style.textContent = `
      html, body { margin:0; padding:0; background:transparent; }
      #email-root, #email-root * {
        font-family: ${this.templateData.TemplateData.FontFamily || ConfigureEmailsHelper.FontFamily};
      }
    `;
    this.root.appendChild(style);

    // Get all images inside the email preview
    const imgs = Array.from(this.root.querySelectorAll('img')) ;
    // If there are no images at all, we can scale immediately and stop.
    if (imgs.length === 0) {
      this.AdjustScale();
      return;
    }
    // This function will keep checking if ALL images have finished loading.
    const check = () => {
      const allDone = imgs.every((img) => img.complete);
      if (allDone) {
        // All images are fully loaded, so now we can safely scale the email preview
        this.AdjustScale();
        return;
      }
      // Not loaded yet → check again on the next animation frame
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
    // Do an initial scale so the user sees something quickly
    this.AdjustScale();
  }

  // function for scaling the SendGridTemplate to its respective container dimensions
  private AdjustScale() {
    const container = this.$refs.host as HTMLElement;
    const content   = this.root?.querySelector('#email-root') as HTMLElement;
    if (!container || !content) {
      return;
    }

    // Reset transforms before measuring (otherwise width/height become distorted).
    content.style.transform = '';
    content.style.transformOrigin = 'top left';
    content.style.position = '';
    // Container dimensions (frame)
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    // Content dimensions (actual email HTML)
    const w  = content.scrollWidth;
    const h  = content.scrollHeight;

    if (this.fitToContainer) {
    // Contain: scale so BOTH dimensions fit, and center it
      const scale = Math.min(cw / w, ch / h, 1);
      const x = Math.max(0, (cw - w * scale) / 2);
      const y = Math.max(0, (ch - h * scale) / 2);

      (container ).style.position = 'relative';
      content.style.position = 'absolute';
      content.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;

      // DON'T expand the host’s height in contain mode
      container.style.height = '100%';
    } else {
    // Editor/thumbnail mode: keep your old width-only logic and grow the host
      let scale = 1;
      // Scale only if content is wider than available preview width
      if (w > cw) {
        scale = cw / w;
        content.style.transform = `scale(${scale})`;
      }
      // Adjust container height so scrollbar tracks scaled content properly
      container.style.height = `${h * scale + this.bottomMargin}px`;
    }
  }

  // #endregion
  // Remove all the observers
  private beforeUnmount(){
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  // Watchers for managing changes in the templateData prop
  @Watch('templateData', { deep: true })
  private OnDataChange() {
    this.RenderShadow();
  }

}
export default toNative(EmailTemplateRenderer);
